<template>
	<StoryblokComponent
		v-if="story?.content"
		:key="storyRenderKey"
		:blok="story.content"
	/>
</template>

<script lang="ts" setup>
	import {useRouteParams} from '@vueuse/router';
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {PageTypeGeneralStoryblok, PageTypeRestaurantStoryblok} from '~/storyblok/types';
	import type {BreadcrumbItem} from '~/types/breadcrumbs';
	import {getBranchStoryblokSlug} from '~/utils/branch';

	type RestaurantPageStoryblok = PageTypeGeneralStoryblok | PageTypeRestaurantStoryblok;

	definePageMeta({title: 'Služby - restaurace | Globus'});

	const branch = useRouteParams<string>('branch');
	const fullSlug = computed(() => getBranchStoryblokSlug(branch.value, 'sluzby/restaurace'));

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {hypermarketsBySlug} = storeToRefs(hypermarketsStore);

	const breadcrumbs = computed<BreadcrumbItem[]>(() => {
		const hypermarket = hypermarketsBySlug.value[branch.value];
		const hypermarketSlug = hypermarket?.slug ?? 'globus';

		return [
			{
				id: 0,
				parent_id: null,
				slug: hypermarketSlug,
				name: hypermarket?.content?.name || 'Globus',
			},
			{
				id: 1,
				parent_id: 0,
				slug: `/${hypermarketSlug}/sluzby`,
				name: 'Služby',
			},
			{
				id: 2,
				parent_id: 1,
				slug: `/${hypermarketSlug}/sluzby/restaurace`,
				name: 'Restaurace',
			},
		];
	});

	provide('breadcrumbs', breadcrumbs);

	const {data: story, error} = await useSingleStoryFetch<RestaurantPageStoryblok>(
		fullSlug,
		{},
		{watch: [fullSlug]},
	);

	const storyRenderKey = computed(
		() => `${fullSlug.value}-${story.value?.content?.component ?? ''}`,
	);

	if (!story.value || error.value) {
		await tryDynamicRedirectOrShowError(error);
	}

	useMetaTitle(computed(() => `${story.value?.content?.title || 'Restaurace'} | Globus`));
</script>
